import React, { useEffect, useMemo, useState } from "react";
import FormDialog from "../../../../components/common/FormDialog";
import FormField from "../../../../components/common/FormField";
import ChipAutocomplete from "../../../../components/common/ChipAutocomplete";
import useFormValidation from "../../../../hooks/useFormValidation";
import { required } from "../../../../utils/formValidation";

const schema = {
  name: [required("Permission name is required.")],
};

const normalizeScope = (value) => String(value || "").trim();

const toScopeList = (values = []) =>
  values.map(normalizeScope).filter(Boolean);

const findConflicts = (allowList = [], denyList = []) => {
  const denySet = new Set(denyList.map((v) => v.toLowerCase()));
  return allowList.filter((item) => denySet.has(item.toLowerCase()));
};

/** Remove entries from `primary` that also appear in `other` (case-insensitive). */
const withoutOverlap = (primary = [], other = []) => {
  const otherSet = new Set(other.map((v) => v.toLowerCase()));
  return primary.filter((item) => !otherSet.has(item.toLowerCase()));
};

/**
 * Create: name only (Postman POST /permission { name }).
 * Edit: name + allow + deny (Postman PUT /permission/{id}).
 * A scope cannot be in both Allow and Deny.
 */
const PermissionFormDialog = ({
  open,
  mode = "create",
  permission = null,
  suggestions = [],
  loading = false,
  onClose,
  onSubmit,
}) => {
  const isCreate = mode === "create";
  const [formError, setFormError] = useState("");
  const [allow, setAllow] = useState([]);
  const [deny, setDeny] = useState([]);

  const { values, getFieldProps, validateAll, reset, isValid } =
    useFormValidation({ name: "" }, schema);

  useEffect(() => {
    if (!open) return;
    setFormError("");
    reset({ name: permission?.name || "" });
    const nextAllow = toScopeList(permission?.allow);
    // Prefer Allow when loaded data already has overlaps.
    const nextDeny = withoutOverlap(toScopeList(permission?.deny), nextAllow);
    setAllow(nextAllow);
    setDeny(nextDeny);
  }, [open, permission, reset]);

  const allowOptions = useMemo(
    () =>
      Array.from(
        new Set(
          [...(suggestions || []), ...allow]
            .map(normalizeScope)
            .filter(Boolean)
            .filter(
              (item) =>
                !deny.some((d) => d.toLowerCase() === item.toLowerCase())
            )
        )
      ),
    [allow, deny, suggestions]
  );

  const denyOptions = useMemo(
    () =>
      Array.from(
        new Set(
          [...(suggestions || []), ...deny]
            .map(normalizeScope)
            .filter(Boolean)
            .filter(
              (item) =>
                !allow.some((a) => a.toLowerCase() === item.toLowerCase())
            )
        )
      ),
    [allow, deny, suggestions]
  );

  const canSubmit = Boolean(!loading && isValid && values.name?.trim());

  const handleAllowChange = (next) => {
    const nextAllow = toScopeList(next);
    setAllow(nextAllow);
    setDeny((prev) => withoutOverlap(prev, nextAllow));
    setFormError("");
  };

  const handleDenyChange = (next) => {
    const nextDeny = toScopeList(next);
    setDeny(nextDeny);
    setAllow((prev) => withoutOverlap(prev, nextDeny));
    setFormError("");
  };

  const handleSubmit = async () => {
    const { isValid: ok, errors } = validateAll();
    if (!ok) {
      setFormError(errors.name || "Please correct the highlighted fields.");
      return;
    }

    const nextAllow = toScopeList(allow);
    const nextDeny = toScopeList(deny);
    const conflicts = findConflicts(nextAllow, nextDeny);
    if (conflicts.length) {
      setFormError(
        `Cannot be in both Allow and Deny: ${conflicts.join(", ")}`
      );
      return;
    }

    setFormError("");
    try {
      if (isCreate) {
        await onSubmit({ name: values.name.trim() });
      } else {
        await onSubmit({
          name: values.name.trim(),
          allow: nextAllow,
          deny: nextDeny,
        });
      }
    } catch (err) {
      setFormError(err?.message || "Unable to save permission.");
    }
  };

  return (
    <FormDialog
      open={open}
      title={isCreate ? "Add Permission" : "Edit Permission"}
      onClose={onClose}
      onSubmit={handleSubmit}
      loading={loading}
      disableSubmit={!canSubmit}
      error={formError}
      onClearError={() => setFormError("")}
      submitLabel={isCreate ? "Create Permission" : "Save Changes"}
      loadingLabel="Saving..."
    >
      <FormField
        label="Permission Name"
        required
        disabled={!isCreate || loading}
        helperText={
          isCreate
            ? "Allow/Deny can be set after create from Permission Details → Edit."
            : undefined
        }
        {...getFieldProps("name")}
      />
      {!isCreate ? (
        <>
          <ChipAutocomplete
            label="Allow"
            freeSolo
            options={allowOptions}
            value={allow}
            onChange={handleAllowChange}
            disabled={loading}
            helperText="Scopes/resources this permission allows (optional). Cannot overlap Deny."
          />
          <ChipAutocomplete
            label="Deny"
            freeSolo
            options={denyOptions}
            value={deny}
            onChange={handleDenyChange}
            disabled={loading}
            helperText="Optional. Same value cannot appear in Allow."
          />
        </>
      ) : null}
    </FormDialog>
  );
};

export default PermissionFormDialog;
